"use client";

import { useRef } from "react";
import type { SCIENTIFIC_CALCULATOR_STRINGS } from "@/components/tools/scientific-calculator";
import type {
  KeyKind,
  MemoryOperation,
} from "@/lib/tool-kit/arithmetic/scientific-session";

type Strings = typeof SCIENTIFIC_CALCULATOR_STRINGS;
type ControlWord = keyof Strings["controls"];

export type KeyAction =
  | { type: "insert"; text: string; kind: KeyKind }
  | { type: "clear" }
  | { type: "backspace" }
  | { type: "equals" }
  | { type: "second" }
  | { type: "memory"; operation: MemoryOperation };

interface KeyDef {
  /** Identifies the key for its accessible name. */
  id: string;
  label?: string;
  word?: ControlWord;
  action: KeyAction;
  look: "fn" | "digit" | "op" | "equals" | "memory";
  /** The key's second function, shown when "2nd" is on. */
  alt?: { id: string; label: string; action: KeyAction };
}

const fn = (text: string): KeyAction => ({
  type: "insert",
  text,
  kind: "function",
});
const op = (text: string): KeyAction => ({
  type: "insert",
  text,
  kind: "operator",
});
const digit = (text: string): KeyAction => ({
  type: "insert",
  text,
  kind: "digit",
});
const constant = (text: string): KeyAction => ({
  type: "insert",
  text,
  kind: "constant",
});

export const FUNCTION_KEYS: readonly KeyDef[] = [
  { id: "second", word: "second", action: { type: "second" }, look: "fn" },
  {
    id: "sin",
    label: "sin",
    action: fn("sin("),
    look: "fn",
    alt: { id: "asin", label: "sin⁻¹", action: fn("asin(") },
  },
  {
    id: "cos",
    label: "cos",
    action: fn("cos("),
    look: "fn",
    alt: { id: "acos", label: "cos⁻¹", action: fn("acos(") },
  },
  {
    id: "tan",
    label: "tan",
    action: fn("tan("),
    look: "fn",
    alt: { id: "atan", label: "tan⁻¹", action: fn("atan(") },
  },
  {
    id: "ln",
    label: "ln",
    action: fn("ln("),
    look: "fn",
    alt: { id: "exp", label: "eˣ", action: fn("exp(") },
  },
  {
    id: "log",
    label: "log",
    action: fn("log("),
    look: "fn",
    alt: { id: "tenPower", label: "10ˣ", action: fn("10^(") },
  },
  { id: "pi", label: "π", action: constant("π"), look: "fn" },
  { id: "e", label: "e", action: constant("e"), look: "fn" },
  {
    id: "square",
    label: "x²",
    action: op("^2"),
    look: "fn",
    alt: { id: "cube", label: "x³", action: op("^3") },
  },
  { id: "power", label: "xʸ", action: op("^"), look: "fn" },
  {
    id: "sqrt",
    label: "√",
    action: fn("√("),
    look: "fn",
    alt: { id: "cbrt", label: "∛", action: fn("cbrt(") },
  },
  { id: "reciprocal", label: "1/x", action: fn("1/("), look: "fn" },
  { id: "open", label: "(", action: digit("("), look: "fn" },
  { id: "close", label: ")", action: op(")"), look: "fn" },
  {
    id: "factorial",
    label: "n!",
    action: op("!"),
    look: "fn",
    alt: { id: "choose", label: "nCr", action: fn("nCr(") },
  },
  { id: "percent", label: "%", action: op("%"), look: "fn" },
  { id: "abs", label: "|x|", action: fn("abs("), look: "fn" },
  { id: "times10", label: "×10ˣ", action: op("×10^"), look: "fn" },
];

export const PAD_KEYS: readonly KeyDef[] = [
  { id: "clear", word: "ac", action: { type: "clear" }, look: "op" },
  { id: "backspace", word: "del", action: { type: "backspace" }, look: "op" },
  { id: "ans", word: "ans", action: constant("ans"), look: "op" },
  { id: "divide", label: "÷", action: op("÷"), look: "op" },
  { id: "7", label: "7", action: digit("7"), look: "digit" },
  { id: "8", label: "8", action: digit("8"), look: "digit" },
  { id: "9", label: "9", action: digit("9"), look: "digit" },
  { id: "multiply", label: "×", action: op("×"), look: "op" },
  { id: "4", label: "4", action: digit("4"), look: "digit" },
  { id: "5", label: "5", action: digit("5"), look: "digit" },
  { id: "6", label: "6", action: digit("6"), look: "digit" },
  { id: "subtract", label: "−", action: op("−"), look: "op" },
  { id: "1", label: "1", action: digit("1"), look: "digit" },
  { id: "2", label: "2", action: digit("2"), look: "digit" },
  { id: "3", label: "3", action: digit("3"), look: "digit" },
  { id: "add", label: "+", action: op("+"), look: "op" },
  { id: "0", label: "0", action: digit("0"), look: "digit" },
  { id: "point", label: ".", action: digit("."), look: "digit" },
  { id: "negate", label: "(−)", action: digit("−"), look: "digit" },
  { id: "equals", label: "=", action: { type: "equals" }, look: "equals" },
];

export const MEMORY_KEYS: readonly KeyDef[] = [
  {
    id: "mc",
    word: "mc",
    action: { type: "memory", operation: "clear" },
    look: "memory",
  },
  {
    id: "mr",
    word: "mr",
    action: { type: "memory", operation: "recall" },
    look: "memory",
  },
  {
    id: "mplus",
    word: "mplus",
    action: { type: "memory", operation: "add" },
    look: "memory",
  },
  {
    id: "mminus",
    word: "mminus",
    action: { type: "memory", operation: "subtract" },
    look: "memory",
  },
  {
    id: "ms",
    word: "ms",
    action: { type: "memory", operation: "store" },
    look: "memory",
  },
];

const BASE =
  "min-h-10 rounded-md border px-2 py-2 font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary";

const LOOKS: Record<KeyDef["look"], string> = {
  fn: `${BASE} border-border bg-muted/40 text-foreground hover:bg-muted`,
  digit: `${BASE} border-border bg-card text-foreground hover:bg-muted`,
  op: `${BASE} border-border bg-muted text-foreground hover:bg-border`,
  equals: `${BASE} border-primary bg-primary text-primary-foreground hover:opacity-90`,
  memory: `${BASE} border-border bg-card text-muted-foreground text-xs hover:bg-muted`,
};

function KeyButton({
  def,
  second,
  strings,
  onPress,
}: {
  def: KeyDef;
  second: boolean;
  strings: Strings;
  onPress: (action: KeyAction, pointer: string) => void;
}) {
  const pointer = useRef("keyboard");
  const alt = second ? def.alt : undefined;
  const id = alt ? alt.id : def.id;
  const action = alt ? alt.action : def.action;
  let text = def.label ?? "";
  if (alt) {
    text = alt.label;
  } else if (def.word) {
    text = strings.controls[def.word];
  }
  const names = strings.keyNames as Record<string, string>;
  const toggled = def.action.type === "second" && second;
  return (
    <button
      aria-label={names[id] ?? text}
      aria-pressed={def.action.type === "second" ? second : undefined}
      className={
        toggled
          ? `${LOOKS[def.look]} border-primary bg-primary/10`
          : LOOKS[def.look]
      }
      onClick={() => onPress(action, pointer.current)}
      onMouseDown={(event) => event.preventDefault()}
      onPointerDown={(event) => {
        pointer.current = event.pointerType;
      }}
      type="button"
    >
      {text}
    </button>
  );
}

export function Keypad({
  second,
  strings,
  onPress,
}: {
  second: boolean;
  strings: Strings;
  onPress: (action: KeyAction, pointer: string) => void;
}) {
  return (
    <div className="space-y-3">
      <fieldset className="m-0 grid min-w-0 grid-cols-5 gap-2 border-0 p-0">
        <legend className="sr-only">{strings.groupMemory}</legend>
        {MEMORY_KEYS.map((def) => (
          <KeyButton
            def={def}
            key={def.id}
            onPress={onPress}
            second={second}
            strings={strings}
          />
        ))}
      </fieldset>
      <div className="grid gap-3 lg:grid-cols-[3fr_2fr]">
        <fieldset className="m-0 grid min-w-0 grid-cols-6 gap-2 border-0 p-0">
          <legend className="sr-only">{strings.groupFunctions}</legend>
          {FUNCTION_KEYS.map((def) => (
            <KeyButton
              def={def}
              key={def.id}
              onPress={onPress}
              second={second}
              strings={strings}
            />
          ))}
        </fieldset>
        <fieldset className="m-0 grid min-w-0 grid-cols-4 gap-2 border-0 p-0">
          <legend className="sr-only">{strings.groupNumbers}</legend>
          {PAD_KEYS.map((def) => (
            <KeyButton
              def={def}
              key={def.id}
              onPress={onPress}
              second={second}
              strings={strings}
            />
          ))}
        </fieldset>
      </div>
    </div>
  );
}
